// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/OnboardingV2Page.tsx (L227-248 scroller, column and transcript; the sticky dock)
import { useEffect, useRef, type ReactNode } from 'react';

import { dsVar } from '@/design-system/tokens';
import { cn } from '@/lib/utils';

import { useStickToBottom } from './useStickToBottom';

export interface ChatThreadProps {
  /** The transcript: UserMessage / AgentMessage (with their cards) as direct children. */
  children: ReactNode;
  /** The composer, pinned to the bottom of the column (the website step). Shown only while there is no dock. */
  composer?: ReactNode;
  /** A docked question card: pinned to the bottom of the column, in place of the composer. */
  dock?: ReactNode;
  /** Changes whenever the docked step changes (a new question): its entrance animation restarts. */
  dockKey?: string;
  /**
   * Optional: changes to ask for a scroll to the bottom while the reader is near it. Growth of the column is followed
   * on its own (a ResizeObserver), so a caller does not have to describe what grew.
   */
  scrollKey?: string | number;
  /** The transcript's accessible name. */
  listLabel?: string;
  className?: string;
}

/** The conversation's scrollbar at a fifth of ink: visible, not loud (TP). */
const SCROLLBAR_STYLE = { scrollbarColor: `${dsVar('color.ink-a20')} transparent` } as const;

/** The entrance of a card rising into the dock (TP: keyed by the question id). */
const DOCK_ENTRANCE_CLASS = 'animate-in fade-in slide-in-from-bottom-2 duration-300 motion-reduce:animate-none';

/**
 * The chat layout, as TP's /onboarding-v2 draws it: one scroller, holding one centred column
 * (`mx-auto max-w-[760px] px-6`), holding the transcript and, under it, a dock that sticks to the bottom of the
 * scroller (`sticky bottom-0 mt-auto`, a fade from the page ground behind it). The dock is part of the column's
 * flow, so it never overlays a card and needs no measuring. Stateless about messages: the caller owns the transcript.
 *
 * The `log` is the transcript alone, not the scroller: a question card arriving in the dock is not a line of
 * the conversation, and a screen reader hears each new line once.
 */
export function ChatThread({
  children,
  composer,
  dock,
  dockKey,
  scrollKey,
  listLabel = 'Conversation',
  className,
}: ChatThreadProps) {
  const { listRef, scrollerProps, scrollToBottomIfPinned } = useStickToBottom<HTMLDivElement>(scrollKey);
  const columnRef = useRef<HTMLDivElement>(null);
  // A card that grows in place (live counts, a feed) adds height without changing `scrollKey`: follow it too,
  // while the reader is pinned. No-op where ResizeObserver is missing.
  useEffect(() => {
    const column = columnRef.current;
    if (!column || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => scrollToBottomIfPinned());
    observer.observe(column);
    return () => observer.disconnect();
  }, [scrollToBottomIfPinned]);
  const bottom = dock ?? composer;

  return (
    <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col bg-transparent">
      <div
        data-testid="chat-thread"
        className={cn('relative flex h-full min-h-0 min-w-0 flex-1 flex-col', className)}
      >
        <div
          ref={listRef}
          {...scrollerProps}
          data-testid="chat-thread-scroller"
          className="min-h-0 min-w-0 flex-1 overflow-y-auto"
          style={SCROLLBAR_STYLE}
        >
          <div ref={columnRef} className="mx-auto flex min-h-full w-full max-w-[760px] flex-col px-6">
            <div
              data-testid="chat-thread-list"
              // A log: each new line is announced politely, once. Only additions, so a card whose numbers tick
              // (the ads count) is not read out again on every poll; the lines already there never repeat.
              role="log"
              aria-live="polite"
              aria-relevant="additions"
              aria-label={listLabel}
              className="flex flex-col gap-8 pb-8 pt-12"
            >
              {children}
            </div>
            {bottom ? (
              <div
                data-testid="chat-thread-dock"
                className="sticky bottom-0 mt-auto flex flex-col gap-3 bg-gradient-to-t from-background from-70% to-transparent pb-6 pt-4"
              >
                {dock ? (
                  <div key={dockKey} className={DOCK_ENTRANCE_CLASS}>
                    {dock}
                  </div>
                ) : (
                  <div data-testid="chat-thread-composer" className={DOCK_ENTRANCE_CLASS}>
                    {composer}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
